/**
 * PerinatalDilBadge.jsx — iter1108 (Feb 2026)
 *
 * Small pill-badge shown in Profile (HealthSummaryCard) and Dashboard
 * (DilHeroCard / InsulinBudgetMiniCard) when the user is in a perinatal
 * stage (pregnancy trimesters, lactation, postpartum, preconception).
 *
 * Purpose: transparency. The user's DIL ceiling looks different (higher
 * carb/protein per-kg, stage_modifier x1.02-1.20) because it's designed
 * for that stage — NOT a bug or drift.
 *
 * Data source: reads `user.profile.perinatal.stage` from AuthContext.
 * Falls back to legacy flat `user.profile.perinatalStage`.
 *
 * Props:
 *   • className — extra classes for outer badge (optional)
 *   • variant   — "compact" (just pill) | "full" (pill + tap → modal)
 *   • testid    — optional data-testid override
 */
import React, { useState } from "react";
import { X, Info, Baby } from "lucide-react";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import { useLanguage } from "@/i18n/LanguageContext";

// Stage → label + tuning knobs (mirrors nutramilo-perinatal-dil ceiling.py PERINATAL_STAGES)
// Only shown in the modal for transparency. NOT used for compute — backend is source of truth.
const STAGE_TABLE = {
  preconception:        { bg: "Подготовка за бременност", en: "Preconception",              carb: 0.6,  protein: 1.2,  mod: 1.00, kcal: 0,   pmid: 16768577 },
  pregnancy_t1:         { bg: "Бременност — I триместър", en: "Pregnancy — 1st trimester",  carb: 0.6,  protein: 1.1,  mod: 1.02, kcal: 70,  pmid: 20669500 },
  pregnancy_t2:         { bg: "Бременност — II триместър", en: "Pregnancy — 2nd trimester", carb: 0.7,  protein: 1.2,  mod: 1.10, kcal: 340, pmid: 20669500 },
  pregnancy_t3:         { bg: "Бременност — III триместър", en: "Pregnancy — 3rd trimester", carb: 0.7, protein: 1.3, mod: 1.15, kcal: 452, pmid: 20669500 },
  postpartum:           { bg: "Постпартум (без кърмене)",  en: "Postpartum (non-lactating)", carb: 0.6, protein: 1.4, mod: 1.00, kcal: 0,   pmid: 12449285 },
  lactation_exclusive:  { bg: "Кърмене — изключително",    en: "Exclusive breastfeeding",   carb: 0.8,  protein: 1.3,  mod: 1.20, kcal: 500, pmid: 16205622 },
  lactation_partial:    { bg: "Кърмене — частично",        en: "Partial breastfeeding",     carb: 0.7,  protein: 1.25, mod: 1.12, kcal: 330, pmid: 16205622 },
};

/** Pull perinatal stage from any of the shapes we've seen in the codebase. */
function readPerinatalStage(user) {
  const p = user?.profile || {};
  return p?.perinatal?.stage || p?.perinatalStage || null;
}

/** Pretty stage label per current language. */
function stageLabel(stage, lang) {
  const row = STAGE_TABLE[stage];
  if (!row) return stage;
  return lang === "en" ? row.en : row.bg;
}

const L = {
  bg: {
    badge_short: "Перинатален DIL",
    tooltip: "Твоят инсулинов бюджет е коригиран за текущия ти перинатален етап — това не е грешка.",
    modal_title: "🤰 Защо е различен твоят DIL?",
    modal_lead: "Твоят дневен инсулинов бюджет използва коригираните нива за твоя перинатален етап. По-високите нужди от протеин и въглехидрати са by design, не bug.",
    formula_title: "Формула",
    formula: "DIL = тегло × (въглех. g/kg + 0.69 × белтък g/kg) × етап_модификатор",
    table_title: "Твоят етап",
    row_stage: "Етап",
    row_carbs_per_kg: "Реф. въглех. (g/kg)",
    row_protein_per_kg: "Реф. белтък (g/kg)",
    row_modifier: "Модификатор на етапа",
    row_extra_kcal: "Допълнителни kcal/ден",
    sources: "Източници",
    sources_body: "IOM 2005/2009, DRI 2002, ADA 2024, ACOG. PMID: ",
    close: "Затвори",
    edit_stage: "Смяна на етап →",
    disclaimer: "Образователно съдържание. Не заменя лекарска консултация.",
  },
  en: {
    badge_short: "Perinatal DIL",
    tooltip: "Your insulin budget is adjusted for your current perinatal stage — this is not a bug.",
    modal_title: "🤰 Why does your DIL look different?",
    modal_lead: "Your daily insulin budget uses stage-adjusted per-kg targets. Higher protein & carb allowances are by design, not a bug.",
    formula_title: "Formula",
    formula: "DIL = weight × (carb g/kg + 0.69 × protein g/kg) × stage_modifier",
    table_title: "Your stage",
    row_stage: "Stage",
    row_carbs_per_kg: "Ref. carbs (g/kg)",
    row_protein_per_kg: "Ref. protein (g/kg)",
    row_modifier: "Stage modifier",
    row_extra_kcal: "Extra kcal/day",
    sources: "Sources",
    sources_body: "IOM 2005/2009, DRI 2002, ADA 2024, ACOG. PMID: ",
    close: "Close",
    edit_stage: "Change stage →",
    disclaimer: "Educational only. Does not replace medical consultation.",
  },
};

export default function PerinatalDilBadge({ className = "", variant = "full", testid = "perinatal-dil-badge" }) {
  const { user } = useAuth();
  const { isDark } = useTheme();
  const { language } = useLanguage();
  const lang = language === "en" ? "en" : "bg";
  const t = L[lang];
  const [open, setOpen] = useState(false);

  const stage = readPerinatalStage(user);
  if (!stage) return null;
  const stageData = STAGE_TABLE[stage];
  const label = stageLabel(stage, lang);

  // Pill classes — pink/fuchsia palette to signal maternal context
  const pillCls = isDark
    ? "bg-pink-500/15 border-pink-400/40 text-pink-100 hover:bg-pink-500/25"
    : "bg-pink-50 border-pink-300 text-pink-800 hover:bg-pink-100";

  return (
    <>
      <button
        type="button"
        onClick={() => variant === "full" && setOpen(true)}
        title={t.tooltip}
        data-testid={testid}
        data-stage={stage}
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10.5px] font-bold whitespace-nowrap transition-colors ${pillCls} ${className}`}
      >
        <Baby className="w-3 h-3" />
        <span>🤰 {t.badge_short}</span>
        <span className={`opacity-80 font-semibold ${isDark ? "text-pink-200/85" : "text-pink-700/85"}`}>· {label}</span>
        {variant === "full" && <Info className="w-3 h-3 opacity-70" />}
      </button>

      {open && variant === "full" && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          onClick={() => setOpen(false)}
          data-testid="perinatal-dil-modal"
        >
          <div
            className={`w-full max-w-md rounded-2xl border shadow-2xl overflow-hidden ${
              isDark ? "bg-slate-900 border-pink-500/30" : "bg-white border-pink-300"
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className={`flex items-start justify-between gap-3 p-4 border-b ${
              isDark ? "border-slate-700/50" : "border-slate-200"
            }`}>
              <div className="min-w-0 flex-1">
                <h3 className={`text-base font-extrabold leading-tight ${isDark ? "text-white" : "text-slate-900"}`}>
                  {t.modal_title}
                </h3>
                <p className={`text-[11px] mt-0.5 leading-snug ${isDark ? "text-slate-400" : "text-slate-600"}`}>
                  {t.modal_lead}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                data-testid="perinatal-dil-modal-close"
                className={`shrink-0 p-1 rounded-full transition-colors ${
                  isDark ? "hover:bg-slate-700 text-slate-300" : "hover:bg-slate-100 text-slate-600"
                }`}
                aria-label={t.close}
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Body */}
            <div className="p-4 space-y-3">
              {/* Formula */}
              <div>
                <div className={`text-[9px] uppercase tracking-wider font-bold mb-1 ${isDark ? "text-pink-300" : "text-pink-700"}`}>
                  {t.formula_title}
                </div>
                <div className={`rounded-lg px-2.5 py-2 font-mono text-[11px] leading-snug ${
                  isDark ? "bg-slate-950/60 text-slate-200" : "bg-slate-50 text-slate-700"
                }`}>
                  {t.formula}
                </div>
              </div>

              {/* Stage table */}
              {stageData && (
                <div>
                  <div className={`text-[9px] uppercase tracking-wider font-bold mb-1 ${isDark ? "text-pink-300" : "text-pink-700"}`}>
                    {t.table_title}
                  </div>
                  <div className={`rounded-lg border divide-y text-[11.5px] ${
                    isDark ? "bg-slate-900/50 border-slate-700/60 divide-slate-700/60" : "bg-white border-slate-200 divide-slate-200"
                  }`}>
                    <Row isDark={isDark} label={t.row_stage} value={label} highlight />
                    <Row isDark={isDark} label={t.row_carbs_per_kg} value={stageData.carb} />
                    <Row isDark={isDark} label={t.row_protein_per_kg} value={stageData.protein} />
                    <Row isDark={isDark} label={t.row_modifier} value={`× ${stageData.mod.toFixed(2)}`} />
                    <Row isDark={isDark} label={t.row_extra_kcal} value={stageData.kcal === 0 ? "—" : `+${stageData.kcal}`} />
                  </div>
                </div>
              )}

              {/* Sources */}
              {stageData && (
                <p className={`text-[10.5px] leading-snug ${isDark ? "text-slate-400" : "text-slate-600"}`}>
                  <span className="font-bold">{t.sources}: </span>
                  {t.sources_body}
                  <span className="font-mono">{stageData.pmid}</span>
                </p>
              )}

              <p className={`text-[9.5px] italic ${isDark ? "text-slate-500" : "text-slate-500"}`}>
                {t.disclaimer}
              </p>
            </div>

            {/* Footer */}
            <div className={`flex items-center justify-between gap-2 p-3 border-t ${
              isDark ? "border-slate-700/50 bg-slate-900/50" : "border-slate-200 bg-slate-50"
            }`}>
              <a
                href="/profile?edit=1&tab=body"
                data-testid="perinatal-dil-modal-edit"
                className={`text-[11px] font-semibold underline ${
                  isDark ? "text-pink-300 hover:text-pink-200" : "text-pink-700 hover:text-pink-800"
                }`}
              >
                {t.edit_stage}
              </a>
              <button
                type="button"
                onClick={() => setOpen(false)}
                data-testid="perinatal-dil-modal-close-btn"
                className={`px-3 py-1.5 rounded-full text-[11px] font-bold transition-colors ${
                  isDark ? "bg-pink-500 hover:bg-pink-400 text-white" : "bg-pink-600 hover:bg-pink-700 text-white"
                }`}
              >
                {t.close}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Row({ isDark, label, value, highlight }) {
  return (
    <div className={`flex items-center justify-between px-2.5 py-1.5 ${
      highlight ? (isDark ? "bg-pink-500/10" : "bg-pink-50") : ""
    }`}>
      <span className={isDark ? "text-slate-400" : "text-slate-600"}>{label}</span>
      <span className={`tabular-nums font-bold ${isDark ? "text-white" : "text-slate-900"}`}>{value}</span>
    </div>
  );
}
